import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { API, errMsg, TOPIC_NAMES } from "../lib/api";
import { PageHeader } from "../components/bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const COUNTS = [5, 10, 20, 30, 40, 50];
const DIFFICULTIES = [
  { value: "mixed", label: "Mixed" },
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];
const STATUSES = [
  { value: "all", label: "All questions" },
  { value: "unanswered", label: "Previously unanswered" },
  { value: "answered", label: "Previously answered" },
  { value: "incorrect", label: "Previously incorrect" },
  { value: "correct", label: "Previously correct" },
  { value: "flagged", label: "Flagged questions" },
];

export default function SessionConfig() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [avail, setAvail] = useState(null);
  const [count, setCount] = useState(10);
  const [custom, setCustom] = useState("");
  const [difficulty, setDifficulty] = useState("mixed");
  const [statusFilter, setStatusFilter] = useState("all");
  const [mode, setMode] = useState("quiz");
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    API.get(`/availability?topic=${slug}`).then(({ data }) => setAvail(data))
      .catch((e) => toast.error(errMsg(e)));
  }, [slug]);

  const effectiveCount = custom ? parseInt(custom, 10) || 0 : count;
  const topicName = slug === "all" ? "All topics" : (TOPIC_NAMES[slug] || slug);

  const start = async () => {
    if (effectiveCount < 1) { toast.error("Choose at least 1 question"); return; }
    setStarting(true);
    try {
      const { data } = await API.post("/sessions", {
        topic: slug, count: effectiveCount, difficulty, status_filter: statusFilter, mode,
      });
      sessionStorage.setItem(`pamind_session_${data.session_id}`, JSON.stringify(data));
      navigate(`/app/session/${data.session_id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setStarting(false);
    }
  };

  const statusCount = (v) => {
    if (!avail) return null;
    if (v === "all") return avail.total;
    return avail.status[v] ?? 0;
  };

  return (
    <div data-testid="session-config-page" className="max-w-3xl">
      <button data-testid="back-to-topics-btn" onClick={() => navigate("/app/topics")}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> All topics
      </button>
      <PageHeader title={topicName} sub={avail ? `${avail.total} published questions available` : "Loading availability…"} />

      <div className="space-y-8">
        <section>
          <h3 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-3">Number of questions</h3>
          <div className="flex flex-wrap gap-2.5">
            {COUNTS.map((c) => (
              <button key={c} data-testid={`count-option-${c}`}
                onClick={() => { setCount(c); setCustom(""); }}
                className={`px-5 py-2.5 rounded-md border text-sm font-semibold transition-colors ${
                  !custom && count === c ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:border-primary/50"}`}>
                {c}
              </button>
            ))}
            <Input data-testid="custom-count-input" type="number" min="1" max={avail?.total || 100} placeholder="Custom"
              value={custom} onChange={(e) => setCustom(e.target.value)} className="w-28" />
          </div>
        </section>

        <section>
          <h3 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-3">Difficulty</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {DIFFICULTIES.map((d) => (
              <button key={d.value} data-testid={`difficulty-option-${d.value}`}
                onClick={() => setDifficulty(d.value)}
                className={`px-4 py-3 rounded-md border text-sm font-semibold transition-colors ${
                  difficulty === d.value ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:border-primary/50"}`}>
                {d.label}
                {avail && d.value !== "mixed" && (
                  <span className={`block text-xs font-normal mt-0.5 ${difficulty === d.value ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                    {avail.difficulty[d.value] ?? 0} available
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>

        <section>
          <h3 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-3">Question status</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
            {STATUSES.map((s) => (
              <button key={s.value} data-testid={`status-option-${s.value}`}
                onClick={() => setStatusFilter(s.value)}
                className={`px-4 py-3 rounded-md border text-sm font-semibold text-left transition-colors ${
                  statusFilter === s.value ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:border-primary/50"}`}>
                {s.label}
                {avail && (
                  <span className={`block text-xs font-normal mt-0.5 ${statusFilter === s.value ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                    {statusCount(s.value)} available
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>

        <section>
          <h3 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-3">Mode</h3>
          <div className="grid grid-cols-2 gap-2.5">
            <button data-testid="mode-option-quiz" onClick={() => setMode("quiz")}
              className={`px-4 py-4 rounded-md border text-left transition-colors ${
                mode === "quiz" ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:border-primary/50"}`}>
              <span className="font-semibold text-sm">Quiz mode</span>
              <span className={`block text-xs mt-1 ${mode === "quiz" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                Instant feedback and full explanations after each question
              </span>
            </button>
            <button data-testid="mode-option-test" onClick={() => setMode("test")}
              className={`px-4 py-4 rounded-md border text-left transition-colors ${
                mode === "test" ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:border-primary/50"}`}>
              <span className="font-semibold text-sm">Test mode</span>
              <span className={`block text-xs mt-1 ${mode === "test" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                Exam conditions — score and review at the end
              </span>
            </button>
          </div>
        </section>

        <Button size="lg" data-testid="start-session-submit-btn" onClick={start} disabled={starting || !avail} className="px-10">
          {starting ? "Building your session…" : `Start ${effectiveCount || count} questions`} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
